import React, { useState, useEffect } from 'react';
import { Wallet, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { madridParts } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });
const fmt = (d) => (d ? d.split('-').reverse().slice(0, 2).join('/') : '');

let methodsCache = null;
export const loadPaymentMethods = (force = false) => {
  if (!methodsCache || force) {
    methodsCache = fetch(`${API_URL}/api/admin/bancos/medios`, { headers: auth() }).then(r => (r.ok ? r.json() : []));
  }
  return methodsCache;
};

const PaymentForm = ({ expense, methods, onSaved }) => {
  const [methodId, setMethodId] = useState(expense.payment_method_id || '');
  const [date, setDate] = useState(expense.paid_date || madridParts(new Date().toISOString()).date);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API_URL}/api/admin/bancos/pago/${expense.id}`, {
      method: 'PUT', headers: auth(), body: JSON.stringify({ method_id: methodId, paid_date: date }) });
    const j = await res.json();
    setSaving(false);
    if (!res.ok) return toast.error(j.detail || 'No se pudo guardar');
    toast.success(`Pagada con ${j.payment_method_name}`);
    onSaved({ payment_method_id: j.payment_method_id, payment_method_name: j.payment_method_name, paid_date: j.paid_date });
  };

  const clear = async () => {
    await fetch(`${API_URL}/api/admin/bancos/pago/${expense.id}`, { method: 'DELETE', headers: auth() });
    toast.success('Marcada como sin pagar');
    onSaved({ payment_method_id: null, payment_method_name: null, paid_date: null });
  };

  return (
    <div className="space-y-3" data-testid={`payment-form-${expense.id}`}>
      <p className="text-sm font-semibold text-gray-900">¿Cómo se pagó?</p>
      <div className="grid grid-cols-2 gap-1.5">
        {methods.map(m => (
          <button key={m.id} onClick={() => setMethodId(m.id)} data-testid={`payment-method-${m.id}`}
            className={`h-9 rounded-lg border text-sm font-medium flex items-center justify-center gap-1 transition-colors ${methodId === m.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            {methodId === m.id && <Check size={13} />}{m.name}
          </button>
        ))}
      </div>
      <label className="block text-xs text-gray-500">Fecha del pago
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1 w-full h-9 rounded-lg border px-2 text-sm text-gray-900" data-testid="payment-date" />
      </label>
      <div className="flex gap-2">
        <button onClick={save} disabled={!methodId || !date || saving} data-testid="payment-save-btn"
          className="flex-1 h-9 rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-40 hover:bg-green-700">Guardar pago</button>
        {expense.payment_method_id && (
          <button onClick={clear} className="h-9 px-3 rounded-lg border text-sm text-gray-600 hover:bg-gray-50" data-testid="payment-clear-btn">Sin pagar</button>
        )}
      </div>
    </div>
  );
};

export const PaymentControl = ({ expense, onChange }) => {
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState([]);
  const [state, setState] = useState(expense);
  useEffect(() => setState(expense), [expense]);
  useEffect(() => { if (open) loadPaymentMethods().then(setMethods); }, [open]);
  const paid = !!state.payment_method_id;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`payment-chip-${expense.id}`} title={paid ? `Pagada el ${state.paid_date}` : 'Marcar como pagada'}
          className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${paid ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-amber-100 hover:text-amber-800'}`}>
          <Wallet size={12} />{paid ? `${state.payment_method_name} · ${fmt(state.paid_date)}` : 'Sin pagar'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <PaymentForm expense={state} methods={methods} onSaved={(p) => { setState(s => ({ ...s, ...p })); setOpen(false); onChange?.(p); }} />
      </PopoverContent>
    </Popover>
  );
};
